define((require) => {
    "use strict";

    const _                               = require("underscore");
    const Backdrop                        = require("shared/ui/experience/Backdrop");
    const EditorEvents                    = require("shared/services/EditorEvents");
    const Lightbox                        = require("shared/ui/common/Lightbox");
    const EditorPreviewMenu               = require("ui/editor/EditorPreviewMenu");
    const ExperienceSyncStateView         = require("editor/ui/experience/editor/ExperienceSyncStateView");
    const ImprovedView                    = require("editor/zbone/ImprovedView");
    const Permissions                     = require("shared/security/Permissions");
    const SaveTemplateModalView           = require("ui/experience/SaveTemplateModalView");
    const ExperienceTemplateOptionsView   = require("ui/editor/ExperienceTemplateOptionsView");
    const template                        = require("ui/editor/ExperienceOptionsView.tpl");
    const Tooltip                         = require("ui/common/Tooltip");
    const UrlUtil                         = require("shared/util/UrlUtil");
    const Storage                         = require("shared/services/Storage");

    // https://en.wikipedia.org/wiki/Language_localisation suggests that you use a combination of ISO 639-1 AND ISO 3166-2
    // pattern is language_code-COUNTRY_CODE
    // language codes: https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
    // country codes: https://en.wikipedia.org/wiki/ISO_3166-2   or https://www.nationsonline.org/oneworld/country_code_list.htm

    /**
     *
     * @class ExperienceOptionsView
     *
     */
    return ImprovedView.extend({

        className: "experience-options-container",

        template,

        _isNewDashboardExperience: false,

        templateHelpers() {
            return {
                spectator: this._isSpectator,
                inGroup: !!this.options.group,
                isNewDashboardExperience: this._isNewDashboardExperience,
            };
        },

        ui: {
            showBackdrop: ".show-backdrop",
            backdropIconContainer: ".show-backdrop .img-container",
            backdropIcon: ".show-backdrop .img-container img",
            autoPlayDelay: ".auto-play-delay-input",
            autorunUnits: ".autorun-units",
            autoPlayTransition: ".auto-play-transition",
            backdropPreviewImage: ".backdrop-preview-image",
            backdropContainer: ".backdrop-container",
            multiSceneOptions: ".multi-scene-options",
            languageSelect: ".language-select"
        },

        regions: {
            leftInfo: ".left-info",
            rightInfo: ".right-info",
            templateOptions: ".template-options"
        },

        bindings: {
            ".name-input": {
                observe: "name",
                updateMethod: "text",
                escape: false,
                events: ["blur"],
                onSet(value) {
                    const payload = {
                        triggerOrigin: "EditExperienceVariantNameInputField",
                        updatedProperty: "name",
                        updatedValue: value
                    };

                    this._trackEditorEvent(EditorEvents.constants.UPDATE_VARIANT, payload);

                    return value;
                }
            }

        },

        modelEvents: {
            "change:backdrop": "_updateBackdropImageIcon",
            "add:scenes remove:scenes": "_toggleMultiSceneOptions"
        },

        _isSpectator: null,

        _backdropViewer: null,

        initialize() {
            this._isSpectator = Permissions.Privileges.CREATE.cannot();
            this._isNewDashboardExperience = Storage.Local.get(Storage.Keys.NEW_DASHBOARD_EXP, Storage.Keys.NEW_DASHBOARD_EXP);
        },

        onRender() {
            if (this.options.group) {
                this.$el.addClass("group");
            }
        },

        onShow() {
            this.leftInfo.show(new EditorPreviewMenu({
                model:     this.model,
                project:   this.options.project,
                group:     this.options.group,
                viewModel: this.options.viewModel
            }));

            this.rightInfo.show(new ExperienceSyncStateView({
                model: this.model,
                group: this.options.group,
                showTooltip: true,
                viewModel: this.options.viewModel,
                notifyConcurrentModification: {defaultValue: 1000 * 60 * 15} // 15 min!token
            }));

            this._setupTemplateOptionsView();
            this._toggleMultiSceneOptions();
            this._updateBackdropImageIcon();
        },

        _setupTemplateOptionsView() {
            if (!this._isSpectator && this.options.companySettings.get("templateSharingEnabled")) {
                const {viewModel} = this.options;
                const templateModel = viewModel.get("templateModel");
                const experience = viewModel.get("experience");

                const experienceTemplateOptionsView = new ExperienceTemplateOptionsView({model: templateModel, experience});

                this.listenTo(experienceTemplateOptionsView, "clickTemplateSave", this._onClickTemplatesSave);
                this.templateOptions.show(experienceTemplateOptionsView);
            }
        },

        _updateBackdropImageIcon() {
            const backdropModel = this.model.get("backdrop");
            const backdropType = backdropModel.get("type");

            switch (backdropType) {
                case Backdrop.TYPE.IMAGE:
                    this.ui.backdropPreviewImage.attr("src", UrlUtil.getAbsoluteViewerApiUrl(backdropModel.get("image").url));
                    this.ui.backdropPreviewImage.removeClass("hidden");
                    this.ui.backdropContainer.css("background-color", "transparent");
                    this.ui.backdropContainer.addClass("no-border");
                    break;
                case Backdrop.TYPE.COLOR:
                    this.ui.backdropPreviewImage.addClass("hidden");
                    this.ui.backdropContainer.css("background-color", backdropModel.get("color"));
                    this.ui.backdropContainer.removeClass("no-border");
                    break;
                default:
                    throw new Error(`Expected the type to be either Image or Color, but it was '${backdropType.toString()}'`);
            }

            if (this._isSpectator) {
                Tooltip.add(this.ui.showBackdrop, {
                    content: "Your access level (Spectator) doesn't permit you to edit the backdrop",
                    placement: "bottom",
                    alignCenter: true,
                    offset: {
                        align: "center"
                    },
                    width: "auto",
                    delay: {show: 1, hide: 0}
                });
            }
        },

        _onClickTemplatesSave() {
            const experience = this.viewScope.viewModel.get("experience");

            this._showLightbox({
                view: new SaveTemplateModalView({
                    model: this.options.viewModel.get("templateModel"),
                    experience
                }),
                css: {width: 520},
                experience,
                props: {
                    modalType: "SaveTemplate",
                    triggerOrigin: "SaveTemplateModal",
                    sceneNumber: this.viewScope.viewModel.get("sceneNumber")
                }
            });
        },

        _showLightbox({
            view, css, experience, props
        }) {
            const lightboxOptions = {
                win: false,
                css,
                editorEventProperties: _.extend({
                    experienceId: experience.get("id"),
                    experienceVersion: experience.get("version"),
                    experiencePushedVersion: experience.get("pushedVersion")
                }, props)
            };

            new Lightbox(view, lightboxOptions, this).open();
        },

        _getFallbackUrl() {
            const url = this.model && this.model.get("fallbackImageURL");
            return url ? UrlUtil.getAbsoluteViewerApiUrl(url) : "";
        },

        /**
         * Sends editor event to Mix Panel via Segment.
         * @param {String} eventName The name of the event to be sent.
         * @param {Object} payload An object containing mandatory properties to be sent.
         * @private
         */
        _trackEditorEvent(eventName, payload) {
            const eventProperties = {
                experienceId: this.model.get("id"),
                experienceVersion: this.model.get("version"),
                experiencePushedVersion: this.model.get("pushedVersion"),
                triggerOrigin: payload.triggerOrigin,
                projectId: this.options.project && this.options.project.get("id"),
                updatedProperty: payload.updatedProperty,
                updatedValue: payload.updatedValue
            };

            if (this.options.group) {
                eventProperties.groupId  = this.options.group.get("id");
                eventProperties.groupType = this.options.group.get("type");
                eventProperties.groupVersion = this.options.group.get("version");
                eventProperties.groupPushedVersion = this.options.group.get("pushedVersion");
            }

            EditorEvents.trigger(eventName, eventProperties);
        },

        _toggleMultiSceneOptions() {
            this.ui.multiSceneOptions.toggleClass("hidden", this.model.getNumberOfScenes() === 1);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/ExperienceOptionsView.js